import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import moment from 'moment';

import Input from 'Components/Input';
import Button from 'Components/Button';
import Icon from 'Components/Icon';
import useReducedErrors from 'Shared/hooks/useReducedErrors';
import getMaxDate from 'Shared/util/getMaxDate';

const getCurrentAllocation = (allocations, reason) => {
  return allocations.filter((allocation) => allocation.reasonId === reason.id);
};

const LeaveRequestView = ({
  reasonsData = [],
  allocationsData = [],
  formikProps: {
    handleChange,
    handleSubmit,
    handleBlur,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    setErrors,
    values: { reason, allocation, dateFrom, dateTo, willWorkOn, comment },
    isSubmitting,
  },
}) => {
  const reducedErrors = useReducedErrors(errors);
  const datePickerRef = useRef(null);

  const previousSelectedReason = useRef(null);
  const previousSelectedAllocation = useRef(null);

  const [allocations, setAllocation] = useState(getCurrentAllocation(allocationsData, reasonsData[0]));

  const currentReasonData = useMemo(() => reasonsData.find(({ id }) => +id === +reason), [reason, reasonsData]);
  const findCurrentAllocation = (allocation, allocationsData) =>
    allocation && allocationsData.find(({ id }) => +id === +allocation);

  const availableDays = useMemo(() => {
    if (allocation && allocations.length && currentReasonData?.requiresAllocation) {
      const currentAllocation = findCurrentAllocation(allocation, allocationsData);
      return currentAllocation ? currentAllocation.daysLeft : null;
    } else if (!allocation && currentReasonData && !currentReasonData.requiresAllocation) {
      return 367;
    }
    return null;
  }, [allocation, allocations, currentReasonData, allocationsData]);

  useEffect(() => {
    !allocation &&
      allocations[0] &&
      allocations[0].reasonId === currentReasonData.id &&
      onAllocationChange(allocations[0]);
  });

  const isFormDisabled = useMemo(() => {
    return !dateFrom || !dateTo || !reason || isSubmitting || (currentReasonData.requiresCompensatory && !willWorkOn);
  }, [dateFrom, dateTo, isSubmitting, reason, willWorkOn, currentReasonData.requiresCompensatory]);

  const onAllocationChange = useCallback(
    (newAllocation) => {
      previousSelectedAllocation.current = allocation;

      const findAllocation = allocation && newAllocation && allocationsData.find(({ id }) => +id === +newAllocation.id);
      const findPreviousAllocation =
        previousSelectedAllocation.current &&
        allocationsData.find(({ id }) => +id === +previousSelectedAllocation.current);

      const currentAllocationDaysLeft = findAllocation?.daysLeft;
      const previousAllocationDaysLeft = findPreviousAllocation?.daysLeft;

      if (
        currentAllocationDaysLeft &&
        previousAllocationDaysLeft &&
        currentAllocationDaysLeft < previousAllocationDaysLeft &&
        !(errors.dateFrom && errors.dateTo)
      ) {
        setFieldValue('dateFrom', '');
        setFieldValue('dateTo', '');
      }

      newAllocation && setFieldValue('allocation', newAllocation.id, false);
    },
    [allocation, allocationsData, errors.dateFrom, errors.dateTo, setFieldValue]
  );

  const onReasonChange = useCallback(
    (newReason) => {
      previousSelectedReason.current = reason;

      const currentReason = reasonsData.find(({ id }) => +id === +newReason.id);
      if (currentReason && !currentReason.requiresAllocation) {
        setFieldValue('allocation', null, false);
      }
      setFieldValue('reason', newReason.id, false);
      setAllocation(getCurrentAllocation(allocationsData, currentReason));
      onAllocationChange(getCurrentAllocation(allocationsData, currentReason)[0]);
    },
    [allocationsData, onAllocationChange, reason, reasonsData, setFieldValue]
  );

  const onCommentChange = useCallback(
    (e) => {
      setFieldValue('comment', e.target.value);
    },
    [setFieldValue]
  );

  const onDateChange = useCallback(
    (dateFrom, dateTo) => {
      const diffDates = moment(dateTo).diff(moment(dateFrom), 'days');
      setFieldValue('dateFrom', dateFrom);

      diffDates < availableDays
        ? setFieldValue('dateTo', dateTo)
        : setFieldValue('dateTo', getMaxDate(dateFrom, dateTo, availableDays));

      setFieldTouched('dateTo');
      setFieldTouched('dateFrom');
    },
    [setFieldValue, availableDays, setFieldTouched]
  );

  useEffect(() => {
    setFieldValue('dateFrom', '');
    setFieldValue('dateTo', '');
    !allocation &&
      currentReasonData.requiresAllocation &&
      setAllocation(getCurrentAllocation(allocationsData, reasonsData[0]));
  }, [allocation, allocationsData, currentReasonData, reason, reasonsData, setFieldValue]);

  useEffect(() => {
    window.addEventListener('keydown', (e) => {
      if (e.keyCode === 13) {
        handleSubmit(e);
      }
    });
  }, [handleSubmit]);

  // shouldDisplayFullRange prop's purpose is to display previous data on datePicker input if selected allocation
  // daysLeft is less then previous selected allocation, also there is an error for user for a better UX
  // (it's a bad UX when a user changes allocation and the date stays the same)
  return (
    <form className="form" onSubmit={handleSubmit}>
      <h2 className="form__heading">Vacations date</h2>
      <div className="form__row">
        <div className="form__col">
          <Input
            label="Reason"
            variant="select"
            helper={(touched.reason && reducedErrors.reason) || ''}
            helperVariant={touched.reason && reducedErrors.reason ? 'error' : 'message'}
            callback={onReasonChange}
            options={reasonsData}
            fieldProps={{
              value: reason ? currentReasonData : reasonsData[0],
              name: 'reason',
              placeholder: 'Select...',
              onChange: handleChange('reason'),
              onBlur: handleBlur('reason'),
            }}
          />
        </div>
        <div className="form__col">
          <Input
            label="Allocation"
            dataTestId="allocation-drop-down"
            helper={
              touched.allocation && reducedErrors.allocation
                ? 'Reason must be selected'
                : 'Note: some reasons must be specified without allocations'
            }
            helperVariant={touched.allocation && reducedErrors.allocation ? 'error' : 'message'}
            variant="select"
            disabled={allocations.length === 0}
            callback={onAllocationChange}
            options={allocations}
            fieldProps={{
              value: (() => {
                const currentAllocation = allocations.find(({ id }) => +id === +allocation);
                return currentAllocation && currentReasonData.requiresAllocation ? currentAllocation : null;
              })(),
              name: 'allocation',
              placeholder: 'Select...',
              onChange: handleChange('allocation'),
              onBlur: handleBlur('allocation'),
            }}
          />
        </div>
      </div>
      <div className="form__row">
        <div className="form__col">
          <Input
            label="Dates"
            variant="date"
            helper={(touched.dateFrom && reducedErrors.dateFrom) || (touched.dateTo && reducedErrors.dateTo)}
            helperVariant={
              (touched.dateFrom && reducedErrors.dateFrom) || (touched.dateTo && reducedErrors.dateTo)
                ? 'error'
                : 'message'
            }
            icon={<Icon name="icon-calendar" dataTestId={'icon-calendar'} />}
            disabled={!availableDays}
            ref={datePickerRef}
            fieldProps={{
              previousDayFrom: dateFrom,
              previousDayTo: dateTo,
              shouldDisplayFullRange: (() => {
                const currentAllocation = findCurrentAllocation(allocation, allocationsData);
                const findPreviousAllocation =
                  previousSelectedAllocation.current &&
                  allocationsData.find(({ id }) => +id === +previousSelectedAllocation.current);

                if (currentAllocation?.daysLeft && allocations) {
                  let isUpdate = false;
                  const updateAllocations = allocations.map((item) => {
                    if (item.id === currentAllocation.id && item.daysLeft > currentAllocation.daysLeft) {
                      isUpdate = true;
                      return currentAllocation;
                    } else return item;
                  });
                  isUpdate && setAllocation(updateAllocations);
                } else if (!availableDays && currentAllocation !== '' && allocations.length > 0) {
                  setAllocation([]);
                }

                const currentAllocationDaysLeft = currentAllocation?.daysLeft;
                const previousAllocationDaysLeft = findPreviousAllocation?.daysLeft;

                if (
                  currentAllocationDaysLeft &&
                  previousAllocationDaysLeft &&
                  currentAllocationDaysLeft < previousAllocationDaysLeft &&
                  !(errors.dateFrom && errors.dateTo)
                ) {
                  setErrors({
                    dateFrom: 'Selected days cannot be bigger than in selected allocation',
                    dateTo: 'Selected days cannot be bigger than in selected allocation',
                  });

                  setFieldTouched('dateTo', true, false);
                  setFieldTouched('dateFrom', true, false);
                  return true;
                }

                return false;
              })(),
              readOnly: true,
              onBlur: handleBlur,
              onChange: handleChange,
              onDateChangeCallback: onDateChange,
              availableDays: availableDays,
              currentAllocation: findCurrentAllocation(allocation, allocationsData),
            }}
          />
        </div>
        <div className="form__col">
          <Input
            label="Work day(s)"
            id="work_day"
            disabled={!currentReasonData.requiresCompensatory}
            dataTestId={'work_days'}
            helper={
              (touched.willWorkOn && reducedErrors.willWorkOn) ||
              'Dates must be specified if you plan to work the other day(s)'
            }
            helperVariant={touched.willWorkOn && reducedErrors.willWorkOn ? 'error' : 'message'}
            fieldProps={{
              name: 'willWorkOn',
              value: willWorkOn,
              placeholder: 'Will work on',
              onChange: handleChange('willWorkOn'),
              onBlur: handleBlur('willWorkOn'),
            }}
          />
        </div>
      </div>
      <Input
        className="form__comment"
        variant="textarea"
        placeholder="Add some details"
        label="Comment"
        id="comment"
        helper={'Optional'}
        helperVariant="message"
        callback={onCommentChange}
        fieldProps={{
          name: 'comment',
          value: comment,
          placeholder: 'Add your comment',
          onChange: handleChange('comment'),
          onBlur: handleBlur('comment'),
        }}
      />
      <div className="form__submit-container">
        <Button text="Request" type="submit" disabled={isFormDisabled} dataTestId={'submit-leave-request'} />
      </div>
    </form>
  );
};

export default LeaveRequestView;
